import { Suspense, lazy, useContext, useMemo, useCallback } from 'react';
import { TabContext } from '@/app/providers/TabProvider';
import { TabInstanceContext } from '@/app/providers/TabInstanceContext';
import { componentRegistry } from '@/lib/__generated__/component-registry';
import { ReportLoader } from '@/components/common/ReportLoader';
import { cn } from '@/lib/utils';
import { AnalyticsScopeContext, useBaseAnalyticsScope } from '@/lib/analytics/scope';
import type { RefObject } from 'react';
import type { TabCloseGuard, TabState, ToolRef } from '@/types';

interface TabContentProps {
  tab: TabState;
  isActive: boolean;
}

export function TabContent({ tab, isActive }: TabContentProps) {
  // Null-safe: TabContent is also rendered without a TabProvider (tests).
  const tabContext = useContext(TabContext);
  const setTabDirty = tabContext?.setTabDirty;
  const registerGuard = tabContext?.registerCloseGuard;
  const forceClose = tabContext?.forceCloseTab;
  const tabId = tab.id;
  const instanceValue = useMemo(
    () => ({
      tabId,
      setDirty: (dirty: boolean) => setTabDirty?.(tabId, dirty),
      registerCloseGuard: (guard: TabCloseGuard) => registerGuard?.(tabId, guard) ?? (() => undefined),
      forceCloseTab: () => forceClose?.(tabId),
    }),
    [tabId, setTabDirty, registerGuard, forceClose],
  );
  const LazyComponent = useMemo(() => {
    // Composite report (composite-report-migration.md): a menu item whose
    // layoutType is "composite" routes to CompositeReportTool instead of the
    // ordinary single-report runner. Checked BEFORE the reportKey branch
    // below so it wins - a composite container item still carries a
    // reportKey (the container's own), but has no dataSources of its own to
    // execute directly.
    if (tab.toolConfig?.type === 'report' && tab.toolConfig?.layoutType === 'composite') {
      return lazy(() => import('@/components/report/CompositeReportTool'));
    }
    // Report-load-by-config (Phase 1, §5.2): a menu item that carries a
    // reportKey routes to the one generic report runner instead of the
    // build-time component registry - no per-report componentKey needed.
    // Gated on both type === 'report' AND a present reportKey, so it can
    // never intercept an existing tool/config tab (or a bespoke report
    // tool that never sets reportKey on its menu item).
    if (tab.toolConfig?.type === 'report' && tab.toolConfig?.reportKey) {
      return lazy(() => import('@/components/report/GenericReportTool'));
    }
    const loader = componentRegistry[tab.componentKey];
    if (!loader) return null;
    return lazy(loader);
  }, [tab.componentKey, tab.toolConfig]);

  const baseScope = useBaseAnalyticsScope({
    type: tab.toolConfig?.type,
    reportKey: tab.toolConfig?.reportKey,
    layoutType: tab.toolConfig?.layoutType,
    componentKey: tab.componentKey,
  });

  const handleTitleChange = useCallback((_title: string) => {
    // Future: update tab label dynamically
  }, []);

  const handleStateChange = useCallback(() => {
    // Future: trigger ribbon re-validation
  }, []);

  if (!LazyComponent) {
    return (
      <div className={cn('h-full p-4', !isActive && 'hidden')}>
        <p className="text-muted-foreground">
          Component not found: {tab.componentKey}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('h-full overflow-auto', !isActive && 'hidden')}>
      <TabInstanceContext.Provider value={instanceValue}>
        <AnalyticsScopeContext.Provider value={baseScope}>
        <Suspense fallback={<ReportLoader message="Loading..." />}>
          <LazyComponent
            // TabState.ref is RefObject<ToolRef | null>; the forwardRef tool
            // components are typed RefObject<ToolRef>. Same runtime object,
            // the null is already part of RefObject's own `current` type.
            ref={tab.ref as RefObject<ToolRef>}
            tabId={tab.id}
            toolConfig={tab.toolConfig}
            instanceKey={tab.instanceKey}
            params={tab.params}
            onTitleChange={handleTitleChange}
            onStateChange={handleStateChange}
          />
        </Suspense>
        </AnalyticsScopeContext.Provider>
      </TabInstanceContext.Provider>
    </div>
  );
}
